import React, { useMemo, useState } from 'react';
import { Loader2, Users } from 'lucide-react';
import { useAppDispatch } from '../store/hooks';
import { applyParticipantEdits } from '../store/conversationSlice';
import { getSpeakerColor } from '../config/participants';
import { PARTICIPANT_REVIEW_COPY as COPY } from '../constants/participantReview';
import {
  SAVE_PARTICIPANTS_FALLBACK_ERROR,
  saveParticipantEdits,
} from '../services/participantReviewService';
import {
  diffParticipantEdits,
  hasRoleChange,
  hasSalesperson,
  initialEdits,
  ReviewRosterEntry,
  ReviewRow,
  RowEdit,
  shouldReviewParticipants,
  unnamedParticipantLabel,
} from '../utils/participantReview';
import { Button } from './ui/button';
import ParticipantReviewRow from './ParticipantReviewRow';

interface ParticipantReviewModalProps {
  /** conversationsessions.sessionId — for a bot session, the bot id. */
  sessionId: string;
  rows: ReviewRow[];
  /** Full saved roster, so a salesperson who never spoke still counts. */
  roster?: ReviewRosterEntry[];
  /** Only the session's creator may edit; observers never see the sheet. */
  isCreator: boolean;
  /** Called after Skip, a no-change Save, or a successful Save. */
  onDone: () => void;
}

/**
 * End-of-call "Review participants" sheet for bot sessions: the creator fixes
 * names and roles before the summary. Save sends only what changed
 * (PUT /conversations/sessions/:id/participants) and mirrors it locally so the
 * summary counts the corrected roles. Same layout, copy and rules as both
 * desktop apps — except "Same as…" (merging a split voice), which is for
 * standalone desktop calls only: a bot call's speakers never split.
 */
const ParticipantReviewModal: React.FC<ParticipantReviewModalProps> = ({
  sessionId,
  rows,
  roster = [],
  isCreator,
  onDone,
}) => {
  const dispatch = useAppDispatch();
  const [edits, setEdits] = useState<Record<string, RowEdit>>(() => initialEdits(rows));
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const colorRoster = useMemo(
    () => rows.map((row) => ({ participantId: row.participantId, role: edits[row.participantId]?.role ?? row.role })),
    [rows, edits]
  );

  if (!isCreator || !shouldReviewParticipants(rows)) return null;

  const canSave = hasSalesperson(rows, edits, roster);
  const isRoleChanged = hasRoleChange(rows, edits);

  const updateRow = (participantId: string, change: Partial<RowEdit>) => {
    setEdits((prev) => ({ ...prev, [participantId]: { ...prev[participantId], ...change } }));
  };

  const handleSave = async () => {
    if (!canSave || isSaving) return;
    const changes = diffParticipantEdits(rows, edits);
    if (changes.length === 0) {
      onDone();
      return;
    }
    setIsSaving(true);
    setSaveError(null);
    try {
      await saveParticipantEdits(sessionId, changes);
      dispatch(applyParticipantEdits(changes));
      onDone();
    } catch (error: any) {
      console.error('[ParticipantReviewModal] Saving participant edits failed:', error);
      // apiService rejects with the server's envelope ({ success: false, message });
      // anything else (network failure) gets the generic copy.
      const serverMessage = error?.success === false ? error?.message : undefined;
      setSaveError(serverMessage || SAVE_PARTICIPANTS_FALLBACK_ERROR);
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-panel w-full max-w-[40rem]" role="dialog" aria-modal="true" aria-labelledby="participant-review-title">
        <div className="px-5 py-4 border-b border-hairline">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-signature" />
            <h2 id="participant-review-title" className="heading-4">{COPY.title}</h2>
          </div>
          <p className="text-xs text-ink-muted mt-1">{COPY.subtitle}</p>
        </div>

        <div className="px-5 py-3">
          <ul className="space-y-2 max-h-80 overflow-y-auto">
            {rows.map((row) => {
              const edit = edits[row.participantId] ?? { name: row.name, role: row.role };
              const color = getSpeakerColor({ speaker: edit.role, participantId: row.participantId }, colorRoster);
              return (
                <ParticipantReviewRow
                  key={row.participantId}
                  row={row}
                  edit={edit}
                  rowLabel={row.name || unnamedParticipantLabel(row.participantId)}
                  color={color}
                  isSaving={isSaving}
                  onChange={(change) => updateRow(row.participantId, change)}
                />
              );
            })}
          </ul>

          {!canSave ? (
            <p className="text-xs text-ink-muted mt-3">{COPY.noSalesperson}</p>
          ) : (
            isRoleChanged && <p className="text-xs text-ink-muted mt-3">{COPY.roleChangeNote}</p>
          )}

          {saveError && (
            <div role="alert" className="bg-error-tint border border-error rounded-control px-3 py-2 mt-3">
              <p className="text-xs text-ink">{saveError}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-hairline">
          <Button variant="tertiary" size="sm" onClick={onDone} disabled={isSaving}>
            {COPY.skip}
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={!canSave || isSaving}>
            {isSaving && <Loader2 aria-hidden="true" className="w-3.5 h-3.5 animate-spin" />}
            {isSaving ? COPY.saving : COPY.save}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ParticipantReviewModal;
